/**
 * @fileoverview Main Operator Administration Panel for Quick Reaction (Reacción Rápida 1 vs 1).
 * Allows stadium and room operators to manage visual themes with strict operational validation,
 * control giant screen projections, trigger authoritative 1 vs 1 duels with ergonomic touch buttons (60px+),
 * monitor live telemetry and reaction times, adjust game physics settings, and inspect match history.
 * 
 * @module components/Admin/Games/QuickReaction
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFloatMessage, MESSAGE_TYPE } from '../../../Providers/FloatMessage';
import { SecondaryBase } from '../../Commons/SecondaryBase';
import { Confirm } from '../../../Commons/Modals/Confirm';
import { QuickReactionThemeModal } from './QuickReactionThemeModal';

import {
    GetThemesBL,
    GetConfigBL,
    SaveConfigBL,
    GetMatchHistoryBL,
    DeleteThemeBL,
    CloneThemeBL,
    ShowCoverBL,
    ShowGameSceneBL,
    StartMatchBL,
    ResetMatchBL,
    FinishEarlyBL,
    GetMatchStateBL,
    UpdatePlayersAndModeBL,
    SanitizeRoomId,
    SanitizePlayerName
} from '../../../../utilities/BL/QuickReaction';

import {
    ConnectWebSocketForQuickReactionAdmin,
    DisconnectWebSocketForQuickReactionAdmin
} from '../../../../utilities/WS/Admin/QuickReactionWS';

/**
 * Componente principal de Administración y Consola de Operador para Reacción Rápida (1 vs 1).
 * 
 * @function QuickReaction
 * @param {Object} props - Propiedades del componente.
 * @param {Function} props.getOutOfTheDynamic - Callback para salir de la dinámica y regresar al dashboard.
 * @param {string} [props.roomId] - Identificador GUID de la sala activa.
 * @returns {React.ReactElement} Consola táctil de administración.
 */
export const QuickReaction = ({ getOutOfTheDynamic, roomId: propRoomId }) => {
    const { showMessage } = useFloatMessage();
    const [searchParams] = useSearchParams();

    // Detección robusta, saneada y contextual de la Sala SaaS
    const activeRoomId = useMemo(() => {
        const rawId = (
            propRoomId ||
            searchParams.get('roomId') ||
            (typeof localStorage !== 'undefined' ? localStorage.getItem('active_room_id') : '') ||
            ''
        );
        return SanitizeRoomId(rawId);
    }, [propRoomId, searchParams]);

    // =========================================================================
    // ESTADOS DE RED, CONECTIVIDAD Y CARGA
    // =========================================================================
    const [wsConnectionStatus, setWsConnectionStatus] = useState('connecting');
    const [isLoadingInitialData, setIsLoadingInitialData] = useState(true);
    const [isActionInProgress, setIsActionInProgress] = useState(false);

    // =========================================================================
    // GESTIÓN DE TEMAS VISUALES
    // =========================================================================
    const [themesList, setThemesList] = useState([]);
    const [selectedThemeId, setSelectedThemeId] = useState('');
    const [isThemeModalOpen, setIsThemeModalOpen] = useState(false);
    const [themeToEdit, setThemeToEdit] = useState(null);

    // =========================================================================
    // ESTADO DE LA TELEMETRÍA EN TIEMPO REAL (1 VS 1)
    // =========================================================================
    const [liveState, setLiveState] = useState({
        phase: 'idle', // 'idle' | 'cover' | 'game_ready' | 'countdown' | 'random_wait' | 'trigger_active' | 'finished'
        themeId: null,
        themeName: '',
        countdownSeconds: 0,
        triggerTimestampServerMs: null,
        player1: {
            name: '',
            side: 'player1',
            isConnected: false,
            reactionTimeMs: null,
            earlyFault: false
        },
        player2: {
            name: '',
            side: 'player2',
            isConnected: false,
            reactionTimeMs: null,
            earlyFault: false
        },
        winnerSide: null,
        winnerName: '',
        winReason: '',
        displayMode: 'single_target'
    });

    // =========================================================================
    // NOMBRES PERSONALIZADOS Y MODO DE VISUALIZACIÓN EN PANTALLA GIGANTE
    // =========================================================================
    const [player1CustomName, setPlayer1CustomName] = useState('Jugador 1');
    const [player2CustomName, setPlayer2CustomName] = useState('Jugador 2');
    const [screenDisplayMode, setScreenDisplayMode] = useState('single_target'); // 'single_target' | 'dual_duel'

    const handleSavePlayersAndMode = async (overrideP1, overrideP2, overrideMode) => {
        const p1 = overrideP1 !== undefined ? overrideP1 : player1CustomName;
        const p2 = overrideP2 !== undefined ? overrideP2 : player2CustomName;
        const mode = overrideMode !== undefined ? overrideMode : screenDisplayMode;
        try {
            await UpdatePlayersAndModeBL(activeRoomId, p1, p2, mode);
        } catch (err) {
            console.warn('[QuickReaction] Error al actualizar jugadores y modo:', err);
        }
    };

    const handleSetDisplayMode = async (newMode) => {
        setScreenDisplayMode(newMode);
        await handleSavePlayersAndMode(player1CustomName, player2CustomName, newMode);
        showMessage({
            type: MESSAGE_TYPE.SUCCESS,
            text: `Modo de pantalla cambiado a: ${newMode === 'single_target' ? 'Objetivo Único (1 Personaje)' : 'Duelo Dual (2 Personajes)'}`
        });
    };

    // =========================================================================
    // CONFIGURACIÓN DE JUEGO POR SALA
    // =========================================================================
    const [gameConfig, setGameConfig] = useState({
        enableEarlyFault: false,
        enableSpamCooldown: false,
        spamCooldownMs: 300,
        timingMode: 'ServerArrival', // 'ServerArrival' | 'ClientOffsetSync'
        minWaitDelayMs: 3000,
        maxWaitDelayMs: 6000
    });
    const [isSavingConfig, setIsSavingConfig] = useState(false);

    // =========================================================================
    // HISTORIAL DE DUELOS
    // =========================================================================
    const [matchesHistory, setMatchesHistory] = useState([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);

    // =========================================================================
    // MODAL DE CONFIRMACIÓN
    // =========================================================================
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmModalData, setConfirmModalData] = useState(null);

    /**
     * Abre el modal de confirmación con parámetros personalizables.
     */
    const openConfirmModal = (message, actionToExecute, yesText = 'Sí, continuar', noText = 'Cancelar') => {
        setConfirmModalData({
            message,
            actionToYesButton: actionToExecute,
            textYesButton: yesText,
            textNoButton: noText
        });
        setShowConfirmModal(true);
    };

    // =========================================================================
    // CARGA DE DATOS INICIALES (TEMAS, CONFIGURACIÓN, HISTORIAL, ESTADO EN VIVO)
    // =========================================================================
    const loadThemes = useCallback(async () => {
        try {
            const response = await GetThemesBL(activeRoomId);
            if (response.success && Array.isArray(response.data)) {
                setThemesList(response.data);
                return response.data;
            } else {
                setThemesList([]);
                return [];
            }
        } catch (error) {
            console.error('[QuickReaction] Error al cargar catálogo de temas:', error);
            setThemesList([]);
            return [];
        }
    }, [activeRoomId]);

    const loadConfig = useCallback(async () => {
        if (!activeRoomId) return;
        try {
            const response = await GetConfigBL(activeRoomId);
            if (response.success && response.data) {
                const configData = response.data;
                setGameConfig({
                    enableEarlyFault: Boolean(configData.enableEarlyFault),
                    enableSpamCooldown: Boolean(configData.enableSpamCooldown),
                    spamCooldownMs: Number(configData.spamCooldownMs) || 300,
                    timingMode: configData.timingMode === 2 || configData.timingMode === 'ClientOffsetSync'
                        ? 'ClientOffsetSync'
                        : 'ServerArrival',
                    minWaitDelayMs: Number(configData.minWaitDelayMs) || 3000,
                    maxWaitDelayMs: Number(configData.maxWaitDelayMs) || 6000
                });

                // Si la configuración tiene un tema activo y no tenemos uno seleccionado, inicializarlo
                if (configData.activeThemeId) {
                    setSelectedThemeId((prev) => (prev ? prev : String(configData.activeThemeId)));
                }
            }
        } catch (error) {
            console.error('[QuickReaction] Error al cargar configuración:', error);
        }
    }, [activeRoomId]);

    const loadMatchHistory = useCallback(async () => {
        if (!activeRoomId) return;
        setIsLoadingHistory(true);
        try {
            const response = await GetMatchHistoryBL(activeRoomId, 20);
            if (response.success && Array.isArray(response.data)) {
                setMatchesHistory(response.data);
            } else {
                setMatchesHistory([]);
            }
        } catch (error) {
            console.error('[QuickReaction] Error al cargar historial:', error);
            setMatchesHistory([]);
        } finally {
            setIsLoadingHistory(false);
        }
    }, [activeRoomId]);

    const loadInitialState = useCallback(async () => {
        if (!activeRoomId) return;
        try {
            const stateResponse = await GetMatchStateBL(activeRoomId);
            if (stateResponse.success && stateResponse.data) {
                const s = stateResponse.data;
                const p1Name = s.player1?.name || 'Jugador 1';
                const p2Name = s.player2?.name || 'Jugador 2';
                const mode = s.displayMode || 'single_target';

                setPlayer1CustomName(p1Name);
                setPlayer2CustomName(p2Name);
                setScreenDisplayMode(mode);

                setLiveState({
                    phase: s.phase || s.phaseName || 'idle',
                    themeId: s.themeId || null,
                    themeName: s.themeName || '',
                    countdownSeconds: s.countdownSeconds || 0,
                    triggerTimestampServerMs: s.triggerTimestampServerMs || null,
                    player1: {
                        name: p1Name,
                        side: 'player1',
                        isConnected: Boolean(s.player1?.isConnected),
                        reactionTimeMs: s.player1?.reactionTimeMs ?? s.player1ReactionTimeMs ?? null,
                        earlyFault: Boolean(s.player1?.earlyFault)
                    },
                    player2: {
                        name: p2Name,
                        side: 'player2',
                        isConnected: Boolean(s.player2?.isConnected),
                        reactionTimeMs: s.player2?.reactionTimeMs ?? s.player2ReactionTimeMs ?? null,
                        earlyFault: Boolean(s.player2?.earlyFault)
                    },
                    winnerSide: s.winnerSide || null,
                    winnerName: s.winnerName || '',
                    winReason: s.winReason || '',
                    displayMode: mode
                });

                if (s.themeId) {
                    setSelectedThemeId((prev) => (prev ? prev : String(s.themeId)));
                }
            }
        } catch (error) {
            console.warn('[QuickReaction] Error al consultar estado inicial en memoria:', error);
        }
    }, [activeRoomId]);

    // Efecto de inicialización de datos
    useEffect(() => {
        let isMounted = true;
        setIsLoadingInitialData(true);

        Promise.all([loadThemes(), loadConfig(), loadMatchHistory(), loadInitialState()]).finally(() => {
            if (isMounted) {
                setIsLoadingInitialData(false);
            }
        });

        return () => {
            isMounted = false;
        };
    }, [loadThemes, loadConfig, loadMatchHistory, loadInitialState]);

    // =========================================================================
    // WEBSOCKET SIGNALR: CONEXIÓN Y PROCESAMIENTO DE EVENTOS EN VIVO
    // =========================================================================
    const handleIncomingWebSocketMessage = useCallback(
        (packet) => {
            if (!packet || typeof packet !== 'object') return;

            const eventType = String(packet.type || packet.action || '').toLowerCase().trim();
            const payload = packet.payload || packet.data || packet.state || packet;

            switch (eventType) {
                case 'quick_reaction_state':
                case 'quickreaction_state':
                case 'telemetry':
                case 'state': {
                    setLiveState((prevState) => ({
                        ...prevState,
                        phase: payload.phase || payload.currentState || prevState.phase,
                        themeId: payload.themeId || prevState.themeId,
                        themeName: payload.themeName || prevState.themeName,
                        countdownSeconds: payload.countdownSeconds ?? prevState.countdownSeconds,
                        player1: {
                            name: payload.player1?.name ?? payload.player1Name ?? prevState.player1.name,
                            side: 'player1',
                            isConnected: payload.player1?.isConnected !== undefined
                                ? Boolean(payload.player1.isConnected)
                                : prevState.player1.isConnected,
                            reactionTimeMs: payload.player1?.reactionTimeMs ?? payload.player1ReactionTimeMs ?? prevState.player1.reactionTimeMs,
                            earlyFault: payload.player1?.earlyFault !== undefined
                                ? Boolean(payload.player1.earlyFault)
                                : prevState.player1.earlyFault
                        },
                        player2: {
                            name: payload.player2?.name ?? payload.player2Name ?? prevState.player2.name,
                            side: 'player2',
                            isConnected: payload.player2?.isConnected !== undefined
                                ? Boolean(payload.player2.isConnected)
                                : prevState.player2.isConnected,
                            reactionTimeMs: payload.player2?.reactionTimeMs ?? payload.player2ReactionTimeMs ?? prevState.player2.reactionTimeMs,
                            earlyFault: payload.player2?.earlyFault !== undefined
                                ? Boolean(payload.player2.earlyFault)
                                : prevState.player2.earlyFault
                        },
                        winnerSide: payload.winnerSide ?? prevState.winnerSide,
                        winnerName: payload.winnerName ?? prevState.winnerName,
                        winReason: payload.winReason ?? prevState.winReason,
                        displayMode: payload.displayMode || prevState.displayMode || 'single_target'
                    }));
                    if (payload.displayMode) setScreenDisplayMode(payload.displayMode);
                    break;
                }

                case 'quick_reaction_countdown_tick':
                case 'countdown_tick': {
                    const count = payload.secondsRemaining ?? payload.countdownSeconds ?? payload.count ?? 0;
                    setLiveState((prevState) => ({
                        ...prevState,
                        phase: 'countdown',
                        countdownSeconds: count
                    }));
                    break;
                }

                case 'quick_reaction_random_wait':
                case 'random_wait': {
                    setLiveState((prevState) => ({
                        ...prevState,
                        phase: 'random_wait',
                        countdownSeconds: 0
                    }));
                    break;
                }

                case 'quick_reaction_trigger_active':
                case 'trigger_active': {
                    setLiveState((prevState) => ({
                        ...prevState,
                        phase: 'trigger_active',
                        triggerTimestampServerMs: payload.triggerTimestampServerMs || Date.now()
                    }));
                    break;
                }

                case 'quick_reaction_early_fault':
                case 'early_fault': {
                    const faultySide = String(payload.side || payload.playerSide || '').toLowerCase();
                    setLiveState((prevState) => ({
                        ...prevState,
                        player1: {
                            ...prevState.player1,
                            earlyFault: faultySide.includes('1') || faultySide === 'player1' ? true : prevState.player1.earlyFault
                        },
                        player2: {
                            ...prevState.player2,
                            earlyFault: faultySide.includes('2') || faultySide === 'player2' ? true : prevState.player2.earlyFault
                        }
                    }));
                    showMessage({
                        type: MESSAGE_TYPE.WARNING,
                        text: `⚠️ ¡Falso inicio detectado en ${faultySide.includes('1') ? 'Jugador 1' : 'Jugador 2'}!`
                    });
                    break;
                }

                case 'quick_reaction_match_finished':
                case 'match_finished': {
                    setLiveState((prevState) => ({
                        ...prevState,
                        phase: 'finished',
                        winnerSide: payload.winnerSide || payload.winner || prevState.winnerSide,
                        winnerName: payload.winnerName || prevState.winnerName,
                        winReason: payload.winReason || prevState.winReason,
                        player1: {
                            ...prevState.player1,
                            reactionTimeMs: payload.player1ReactionTimeMs ?? payload.player1?.reactionTimeMs ?? prevState.player1.reactionTimeMs,
                            earlyFault: payload.player1EarlyFault !== undefined
                                ? Boolean(payload.player1EarlyFault)
                                : prevState.player1.earlyFault
                        },
                        player2: {
                            ...prevState.player2,
                            reactionTimeMs: payload.player2ReactionTimeMs ?? payload.player2?.reactionTimeMs ?? prevState.player2.reactionTimeMs,
                            earlyFault: payload.player2EarlyFault !== undefined
                                ? Boolean(payload.player2EarlyFault)
                                : prevState.player2.earlyFault
                        }
                    }));
                    loadMatchHistory();
                    break;
                }

                case 'quick_reaction_player_joined':
                case 'player_joined': {
                    const side = String(payload.side || '').toLowerCase();
                    const rawName = payload.playerName || payload.name || 'Aficionado';
                    const playerName = SanitizePlayerName(rawName) || 'Aficionado';
                    setLiveState((prevState) => ({
                        ...prevState,
                        player1: side.includes('1') || side === 'player1'
                            ? { ...prevState.player1, name: playerName, isConnected: true }
                            : prevState.player1,
                        player2: side.includes('2') || side === 'player2'
                            ? { ...prevState.player2, name: playerName, isConnected: true }
                            : prevState.player2
                    }));
                    showMessage({
                        type: MESSAGE_TYPE.INFO,
                        text: `👤 ${playerName} se unió como ${side.includes('1') ? 'Jugador 1' : 'Jugador 2'}`
                    });
                    break;
                }

                case 'quick_reaction_player_left':
                case 'player_left': {
                    const side = String(payload.side || '').toLowerCase();
                    setLiveState((prevState) => ({
                        ...prevState,
                        player1: side.includes('1') || side === 'player1'
                            ? { ...prevState.player1, isConnected: false }
                            : prevState.player1,
                        player2: side.includes('2') || side === 'player2'
                            ? { ...prevState.player2, isConnected: false }
                            : prevState.player2
                    }));
                    break;
                }

                case 'quick_reaction_cover_shown':
                case 'cover_shown': {
                    setLiveState((prevState) => ({
                        ...prevState,
                        phase: 'cover'
                    }));
                    break;
                }

                case 'quick_reaction_game_scene_shown':
                case 'game_scene_shown':
                case 'quick_reaction_game_shown': {
                    setLiveState((prevState) => ({
                        ...prevState,
                        phase: 'game_ready'
                    }));
                    break;
                }

                case 'quick_reaction_reset':
                case 'quick_reaction_game_ready':
                case 'game_ready': {
                    setLiveState((prevState) => ({
                        ...prevState,
                        phase: 'game_ready',
                        countdownSeconds: 0,
                        triggerTimestampServerMs: null,
                        winnerSide: null,
                        winnerName: '',
                        winReason: '',
                        player1: {
                            ...prevState.player1,
                            reactionTimeMs: null,
                            earlyFault: false
                        },
                        player2: {
                            ...prevState.player2,
                            reactionTimeMs: null,
                            earlyFault: false
                        }
                    }));
                    break;
                }

                case 'quick_reaction_players_updated':
                case 'players_updated': {
                    const p1 = payload.player1Name || payload.player1?.name || 'Jugador 1';
                    const p2 = payload.player2Name || payload.player2?.name || 'Jugador 2';
                    const mode = payload.displayMode || 'single_target';
                    setPlayer1CustomName(p1);
                    setPlayer2CustomName(p2);
                    setScreenDisplayMode(mode);
                    setLiveState((prevState) => ({
                        ...prevState,
                        displayMode: mode,
                        player1: { ...prevState.player1, name: p1 },
                        player2: { ...prevState.player2, name: p2 }
                    }));
                    break;
                }

                default:
                    break;
            }
        },
        [loadMatchHistory, showMessage]
    );

    useEffect(() => {
        ConnectWebSocketForQuickReactionAdmin(
            handleIncomingWebSocketMessage,
            (newStatus) => setWsConnectionStatus(newStatus),
            activeRoomId
        );

        return () => {
            DisconnectWebSocketForQuickReactionAdmin();
        };
    }, [activeRoomId, handleIncomingWebSocketMessage]);

    // =========================================================================
    // ACCIONES DE PROYECCIÓN Y CONTROL DE SALA
    // =========================================================================

    /**
     * Muestra la portada oficial de Reacción Rápida.
     */
    const handleShowCover = async () => {
        if (!selectedThemeId) {
            showMessage({
                type: MESSAGE_TYPE.WARNING,
                text: 'Debes seleccionar un tema visual antes de proyectar la portada.'
            });
            return;
        }

        setIsActionInProgress(true);
        try {
            const response = await ShowCoverBL(activeRoomId, selectedThemeId);
            if (response.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: 'Portada de Reacción Rápida proyectada en pantalla.'
                });
                setLiveState((prev) => ({ ...prev, phase: 'cover' }));
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: response.message || 'Error al proyectar la portada.'
                });
            }
        } catch (error) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de conexión al proyectar la portada.'
            });
        } finally {
            setIsActionInProgress(false);
        }
    };

    /**
     * Prepara y muestra la escena de duelo 1 vs 1.
     */
    const handleShowGame = async () => {
        if (!selectedThemeId) {
            showMessage({
                type: MESSAGE_TYPE.WARNING,
                text: 'Debes seleccionar un tema visual antes de proyectar el juego.'
            });
            return;
        }

        setIsActionInProgress(true);
        try {
            const response = await ShowGameSceneBL(activeRoomId, selectedThemeId);
            if (response.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: 'Escenario de combate proyectado. Esperando jugadores.'
                });
                setLiveState((prev) => ({ ...prev, phase: 'game_ready' }));
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: response.message || 'Error al proyectar el escenario.'
                });
            }
        } catch (error) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de conexión al preparar la escena de juego.'
            });
        } finally {
            setIsActionInProgress(false);
        }
    };

    /**
     * Inicia la secuencia oficial de duelo (Conteo -> Suspenso -> Disparo).
     */
    const handleStartMatch = async () => {
        if (!selectedThemeId) {
            showMessage({
                type: MESSAGE_TYPE.WARNING,
                text: 'Selecciona un tema visual antes de iniciar el duelo.'
            });
            return;
        }

        setIsActionInProgress(true);
        try {
            const response = await StartMatchBL(activeRoomId);
            if (response.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: '¡Secuencia de duelo iniciada! Conteo en progreso...'
                });
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: response.message || 'Error al iniciar la secuencia de duelo.'
                });
            }
        } catch (error) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de red al iniciar el duelo.'
            });
        } finally {
            setIsActionInProgress(false);
        }
    };

    /**
     * Reinicia la ronda para revancha inmediata conservando tema activo y participantes.
     */
    const handleResetMatch = async () => {
        setIsActionInProgress(true);
        try {
            const response = await ResetMatchBL(activeRoomId);
            if (response.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: 'Ronda reiniciada. Listos para la revancha.'
                });
                setLiveState((prev) => ({
                    ...prev,
                    phase: 'game_ready',
                    countdownSeconds: 0,
                    triggerTimestampServerMs: null,
                    winnerSide: null,
                    winnerName: '',
                    winReason: '',
                    player1: { ...prev.player1, reactionTimeMs: null, earlyFault: false },
                    player2: { ...prev.player2, reactionTimeMs: null, earlyFault: false }
                }));
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: response.message || 'Error al reiniciar la ronda.'
                });
            }
        } catch (error) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de red al reiniciar la ronda.'
            });
        } finally {
            setIsActionInProgress(false);
        }
    };

    // =========================================================================
    // CRUD DE TEMAS: NUEVO, EDITAR, CLONAR, ELIMINAR
    // =========================================================================

    const handleOpenNewThemeModal = () => {
        setThemeToEdit(null);
        setIsThemeModalOpen(true);
    };

    const handleOpenEditThemeModal = () => {
        if (!selectedThemeId) {
            showMessage({
                type: MESSAGE_TYPE.WARNING,
                text: 'Selecciona un tema para editarlo.'
            });
            return;
        }

        const foundTheme = themesList.find((t) => String(t.id || t.Id) === String(selectedThemeId));
        if (!foundTheme) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'No se encontró el tema seleccionado.'
            });
            return;
        }

        setThemeToEdit(foundTheme);
        setIsThemeModalOpen(true);
    };

    const handleCloneSelectedTheme = async () => {
        if (!selectedThemeId) {
            showMessage({
                type: MESSAGE_TYPE.WARNING,
                text: 'Selecciona un tema para duplicarlo.'
            });
            return;
        }

        const foundTheme = themesList.find((t) => String(t.id || t.Id) === String(selectedThemeId));
        const themeName = foundTheme ? foundTheme.name || foundTheme.Name : 'Tema';

        openConfirmModal(
            `¿Deseas clonar el tema "${themeName}"? Se creará una copia editable.`,
            async () => {
                try {
                    const cloneResponse = await CloneThemeBL(selectedThemeId, null, activeRoomId);
                    if (cloneResponse.success && cloneResponse.data) {
                        showMessage({
                            type: MESSAGE_TYPE.SUCCESS,
                            text: `Tema clonado exitosamente: "${cloneResponse.data.name || themeName + ' (Copia)'}".`
                        });
                        const updatedList = await loadThemes();
                        const newId = cloneResponse.data.id || cloneResponse.data.Id;
                        if (newId) {
                            setSelectedThemeId(String(newId));
                        }
                        return { success: true };
                    } else {
                        showMessage({
                            type: MESSAGE_TYPE.BAD,
                            text: cloneResponse.message || 'Error al clonar el tema.'
                        });
                        return { success: false, message: cloneResponse.message };
                    }
                } catch (error) {
                    showMessage({
                        type: MESSAGE_TYPE.BAD,
                        text: 'Error inesperado al clonar el tema.'
                    });
                    return { success: false, message: 'Error inesperado.' };
                }
            },
            'Sí, clonar tema'
        );
    };

    const handleDeleteSelectedTheme = async () => {
        if (!selectedThemeId) {
            showMessage({
                type: MESSAGE_TYPE.WARNING,
                text: 'Selecciona un tema para eliminarlo.'
            });
            return;
        }

        const foundTheme = themesList.find((t) => String(t.id || t.Id) === String(selectedThemeId));
        if (foundTheme?.isSystemPreset || foundTheme?.IsSystemPreset) {
            showMessage({
                type: MESSAGE_TYPE.WARNING,
                text: 'Los temas base del sistema no pueden ser eliminados.'
            });
            return;
        }

        const themeName = foundTheme ? foundTheme.name || foundTheme.Name : 'Tema';

        openConfirmModal(
            `¿Estás seguro de eliminar el tema "${themeName}"? Esta acción no se puede deshacer.`,
            async () => {
                try {
                    const deleteResponse = await DeleteThemeBL(selectedThemeId, activeRoomId);
                    if (deleteResponse.success) {
                        showMessage({
                            type: MESSAGE_TYPE.SUCCESS,
                            text: `Tema "${themeName}" eliminado exitosamente.`
                        });
                        setSelectedThemeId('');
                        await loadThemes();
                        return { success: true };
                    } else {
                        showMessage({
                            type: MESSAGE_TYPE.BAD,
                            text: deleteResponse.message || 'Error al eliminar el tema.'
                        });
                        return { success: false, message: deleteResponse.message };
                    }
                } catch (error) {
                    showMessage({
                        type: MESSAGE_TYPE.BAD,
                        text: 'Error de red al eliminar el tema.'
                    });
                    return { success: false, message: 'Error de red.' };
                }
            },
            'Sí, eliminar tema'
        );
    };

    const handleThemeSavedCallback = async (savedTheme) => {
        const updatedList = await loadThemes();
        if (savedTheme && (savedTheme.id || savedTheme.Id)) {
            setSelectedThemeId(String(savedTheme.id || savedTheme.Id));
        }
    };

    // =========================================================================
    // GUARDADO DE CONFIGURACIÓN TOUCH
    // =========================================================================
    const handleSaveConfig = async (e) => {
        if (e) e.preventDefault();
        if (!activeRoomId) {
            showMessage({
                type: MESSAGE_TYPE.WARNING,
                text: 'Identificador de sala no disponible para guardar configuración.'
            });
            return;
        }

        setIsSavingConfig(true);
        try {
            const payload = {
                roomId: activeRoomId,
                activeThemeId: selectedThemeId || null,
                enableEarlyFault: gameConfig.enableEarlyFault,
                enableSpamCooldown: gameConfig.enableSpamCooldown,
                spamCooldownMs: gameConfig.spamCooldownMs,
                timingMode: gameConfig.timingMode === 'ClientOffsetSync' ? 2 : 1,
                minWaitDelayMs: gameConfig.minWaitDelayMs,
                maxWaitDelayMs: gameConfig.maxWaitDelayMs
            };

            const response = await SaveConfigBL(payload, activeRoomId);
            if (response.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: 'Configuración de Reacción Rápida guardada correctamente.'
                });
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: response.message || 'Error al guardar la configuración.'
                });
            }
        } catch (error) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de red al guardar la configuración.'
            });
        } finally {
            setIsSavingConfig(false);
        }
    };

    // =========================================================================
    // COMPUTED HELPERS
    // =========================================================================
    const isThemeSelected = Boolean(selectedThemeId && String(selectedThemeId).trim() !== '');

    const selectedThemeObject = useMemo(() => {
        return themesList.find((t) => String(t.id || t.Id) === String(selectedThemeId)) || null;
    }, [themesList, selectedThemeId]);

    const isDuelActive = useMemo(() => {
        return ['countdown', 'random_wait', 'trigger_active'].includes(liveState.phase);
    }, [liveState.phase]);

    // Helpers para formatear el badge de fase
    const getPhaseBadgeInfo = () => {
        switch (liveState.phase) {
            case 'idle':
                return { label: 'IDLE (EN ESPERA)', className: 'bg-secondary text-white' };
            case 'cover':
                return { label: 'PORTADA PROYECTADA', className: 'bg-info text-dark fw-bold' };
            case 'game_ready':
                return { label: 'ESCENARIO LISTO (1 VS 1)', className: 'bg-primary text-white fw-bold' };
            case 'countdown':
                return {
                    label: `CONTEO REGRESIVO (${liveState.countdownSeconds})`,
                    className: 'bg-warning text-dark fw-bold animate-pulse'
                };
            case 'random_wait':
                return {
                    label: '¡ATENCIÓN! VENTANA DE SUSPENSO',
                    className: 'bg-dark text-warning border border-warning fw-bold'
                };
            case 'trigger_active':
                return {
                    label: '¡¡DISPARO ACTIVO!!',
                    className: 'bg-danger text-white fw-bold animate-bounce fs-6'
                };
            case 'finished':
                return { label: 'DUELO FINALIZADO', className: 'bg-success text-white fw-bold' };
            default:
                return { label: liveState.phase.toUpperCase(), className: 'bg-secondary text-white' };
        }
    };

    const phaseInfo = getPhaseBadgeInfo();

    return (
        <SecondaryBase actionToExecute={getOutOfTheDynamic}>
            <div className="container-fluid px-0 px-md-3 py-2 pb-5 quick-reaction-admin">
                {/* Header Superior: Título, Sala y WebSocket Status */}
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 p-3 bg-white rounded-4 shadow-sm border">
                    <div className="d-flex align-items-center gap-3">
                        <div
                            className="bg-danger text-white rounded-3 d-flex align-items-center justify-content-center shadow-sm"
                            style={{ width: '52px', height: '52px', fontSize: '1.6rem' }}
                        >
                            <i className="bi bi-lightning-charge-fill"></i>
                        </div>
                        <div>
                            <h3 className="m-0 fw-bold text-dark">Reacción Rápida 1 vs 1</h3>
                            <span className="small text-muted">Consola de Control del Operador</span>
                        </div>
                    </div>

                    <div className="d-flex align-items-center gap-2 mt-2 mt-sm-0">
                        <span
                            className={`badge px-3 py-2 rounded-pill d-flex align-items-center gap-1 ${
                                wsConnectionStatus === 'connected'
                                    ? 'bg-success-subtle text-success border border-success'
                                    : wsConnectionStatus === 'connecting' || wsConnectionStatus === 'reconnecting'
                                    ? 'bg-warning-subtle text-warning border border-warning'
                                    : 'bg-danger-subtle text-danger border border-danger'
                            }`}
                        >
                            <span
                                className={`rounded-circle d-inline-block ${
                                    wsConnectionStatus === 'connected'
                                        ? 'bg-success'
                                        : wsConnectionStatus === 'connecting'
                                        ? 'bg-warning'
                                        : 'bg-danger'
                                }`}
                                style={{ width: '8px', height: '8px' }}
                            ></span>
                            <span className="text-uppercase fw-semibold" style={{ fontSize: '0.75rem' }}>
                                SignalR: {wsConnectionStatus}
                            </span>
                        </span>

                        {activeRoomId && (
                            <span className="badge bg-light text-secondary border px-3 py-2 rounded-pill small font-monospace">
                                Sala: {activeRoomId.substring(0, 8)}...
                            </span>
                        )}
                    </div>
                </div>

                {/* ============================================================ */}
                {/* TARJETA 1: SELECTOR Y GESTIÓN DE TEMAS VISUALES               */}
                {/* ============================================================ */}
                <div className="card border-0 shadow-sm rounded-4 mb-4 overflow-hidden">
                    <div className="card-header bg-dark text-white p-3 px-4 d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-palette-fill text-warning fs-5"></i>
                            <h5 className="m-0 fw-bold">1. Selección y Gestión de Tema Visual</h5>
                        </div>
                        {selectedThemeObject && (
                            <span className="badge bg-warning text-dark fw-bold">
                                Tema Activo: {selectedThemeObject.name || selectedThemeObject.Name}
                            </span>
                        )}
                    </div>

                    <div className="card-body p-4 bg-light">
                        {/* Alerta de bloqueo si no hay tema seleccionado */}
                        {!isThemeSelected && (
                            <div className="alert alert-warning border-warning d-flex align-items-center gap-3 mb-3 shadow-sm">
                                <i className="bi bi-exclamation-triangle-fill fs-3 text-warning"></i>
                                <div>
                                    <h6 className="fw-bold mb-1">Selección de Tema Obligatoria</h6>
                                    <p className="mb-0 small">
                                        Debes seleccionar un tema visual del catálogo para desbloquear los controles de
                                        proyección en pantalla gigante y el botón de inicio de duelo.
                                    </p>
                                </div>
                            </div>
                        )}

                        <div className="row g-3 align-items-center">
                            <div className="col-12 col-lg-6">
                                <label className="form-label fw-bold text-dark small mb-1">
                                    Catálogo de Temas Disponibles:
                                </label>
                                <select
                                    className={`form-select form-select-lg border-2 fw-semibold ${
                                        !isThemeSelected ? 'border-warning' : 'border-success'
                                    }`}
                                    value={selectedThemeId}
                                    onChange={(e) => setSelectedThemeId(e.target.value)}
                                    style={{ minHeight: '52px' }}
                                >
                                    <option value="">-- Selecciona un tema... --</option>
                                    {themesList.map((t) => {
                                        const tId = String(t.id || t.Id);
                                        const tName = t.name || t.Name;
                                        const isPreset = Boolean(t.isSystemPreset || t.IsSystemPreset);
                                        return (
                                            <option key={tId} value={tId}>
                                                {tName} {isPreset ? '(Preset Sistema)' : ''}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>

                            <div className="col-12 col-lg-6 d-flex flex-wrap gap-2 justify-content-lg-end">
                                <button
                                    type="button"
                                    className="btn btn-primary px-3 fw-bold d-flex align-items-center gap-1"
                                    onClick={handleOpenNewThemeModal}
                                    style={{ minHeight: '48px' }}
                                >
                                    <i className="bi bi-plus-circle-fill"></i>
                                    <span>Nuevo Tema</span>
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-outline-dark px-3 fw-semibold d-flex align-items-center gap-1"
                                    onClick={handleOpenEditThemeModal}
                                    disabled={!isThemeSelected}
                                    style={{ minHeight: '48px' }}
                                >
                                    <i className="bi bi-pencil-square"></i>
                                    <span>Editar Tema</span>
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-outline-secondary px-3 fw-semibold d-flex align-items-center gap-1"
                                    onClick={handleCloneSelectedTheme}
                                    disabled={!isThemeSelected}
                                    style={{ minHeight: '48px' }}
                                >
                                    <i className="bi bi-copy"></i>
                                    <span>Clonar</span>
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-outline-danger px-3 fw-semibold d-flex align-items-center gap-1"
                                    onClick={handleDeleteSelectedTheme}
                                    disabled={!isThemeSelected || Boolean(selectedThemeObject?.isSystemPreset || selectedThemeObject?.IsSystemPreset)}
                                    style={{ minHeight: '48px' }}
                                >
                                    <i className="bi bi-trash3"></i>
                                    <span>Eliminar</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ============================================================ */}
                {/* TARJETA 2 & 3: CONTROLES DE PROYECCIÓN Y BOTÓN DUELO (TOUCH) */}
                {/* ============================================================ */}
                <div className="row g-4 mb-4">
                    {/* Controles de Proyección */}
                    <div className="col-12 col-lg-5">
                        <div className="card border-0 shadow-sm rounded-4 h-100">
                            <div className="card-header bg-white p-3 px-4 border-bottom">
                                <div className="d-flex align-items-center gap-2">
                                    <i className="bi bi-display text-primary fs-5"></i>
                                    <h5 className="m-0 fw-bold text-dark">2. Proyección Pantalla</h5>
                                </div>
                            </div>
                            <div className="card-body p-4 d-flex flex-column justify-content-center gap-3">
                                <button
                                    type="button"
                                    className="btn btn-outline-primary btn-lg fw-bold d-flex align-items-center justify-content-center gap-2 w-100"
                                    onClick={handleShowCover}
                                    disabled={!isThemeSelected || isActionInProgress}
                                    style={{ minHeight: '60px', touchAction: 'manipulation' }}
                                >
                                    <i className="bi bi-image fs-4"></i>
                                    <span>Mostrar Portada</span>
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-primary btn-lg fw-bold d-flex align-items-center justify-content-center gap-2 w-100"
                                    onClick={handleShowGame}
                                    disabled={!isThemeSelected || isActionInProgress}
                                    style={{ minHeight: '60px', touchAction: 'manipulation' }}
                                >
                                    <i className="bi bi-controller fs-4"></i>
                                    <span>Mostrar Juego (1 vs 1)</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Controles de Duelo Touch (Ergonomía 60px+) */}
                    <div className="col-12 col-lg-7">
                        <div className="card border-0 shadow-sm rounded-4 h-100 bg-dark text-white">
                            <div className="card-header bg-transparent p-3 px-4 border-bottom border-secondary d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center gap-2">
                                    <i className="bi bi-crosshair text-danger fs-5"></i>
                                    <h5 className="m-0 fw-bold">3. Control de Duelo Táctil</h5>
                                </div>
                                <span className={`badge ${phaseInfo.className}`}>{phaseInfo.label}</span>
                            </div>

                            <div className="card-body p-4 d-flex flex-column justify-content-center gap-3">
                                {/* Botón Gigante INICIAR DUELO */}
                                <button
                                    type="button"
                                    className={`btn btn-lg fw-bolder d-flex align-items-center justify-content-center gap-3 w-100 shadow ${
                                        !isThemeSelected || isDuelActive
                                            ? 'btn-secondary text-white-50'
                                            : 'btn-danger text-white'
                                    }`}
                                    onClick={handleStartMatch}
                                    disabled={!isThemeSelected || isDuelActive || isActionInProgress}
                                    style={{
                                        minHeight: '75px',
                                        fontSize: '1.4rem',
                                        letterSpacing: '1px',
                                        touchAction: 'manipulation'
                                    }}
                                >
                                    {isActionInProgress ? (
                                        <div className="spinner-border spinner-border-sm" role="status"></div>
                                    ) : (
                                        <i className="bi bi-play-circle-fill fs-2"></i>
                                    )}
                                    <span>
                                        {isDuelActive
                                            ? 'DUELO EN PROGRESO...'
                                            : !isThemeSelected
                                            ? 'SELECCIONA UN TEMA'
                                            : '¡INICIAR DUELO (3, 2, 1) !'}
                                    </span>
                                </button>

                                <div className="row g-2">
                                    <div className="col-12 col-sm-6">
                                        <button
                                            type="button"
                                            className="btn btn-warning text-dark fw-bold d-flex align-items-center justify-content-center gap-2 w-100"
                                            onClick={handleResetMatch}
                                            disabled={isActionInProgress}
                                            style={{ minHeight: '56px', touchAction: 'manipulation' }}
                                        >
                                            <i className="bi bi-arrow-repeat fs-5"></i>
                                            <span>Reiniciar Ronda (Revancha)</span>
                                        </button>
                                    </div>
                                    <div className="col-12 col-sm-6">
                                        <button
                                            type="button"
                                            className="btn btn-outline-light fw-bold d-flex align-items-center justify-content-center gap-2 w-100"
                                            onClick={handleShowCover}
                                            disabled={!isThemeSelected || isActionInProgress}
                                            style={{ minHeight: '56px', touchAction: 'manipulation' }}
                                        >
                                            <i className="bi bi-box-arrow-in-up-left fs-5"></i>
                                            <span>Volver a Portada</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ============================================================ */}
                {/* TARJETA 4: MONITOR DE TELEMETRÍA EN VIVO (1 VS 1)             */}
                {/* ============================================================ */}
                <div className="card border-0 shadow-sm rounded-4 mb-4 overflow-hidden">
                    <div className="card-header bg-primary text-white p-3 px-4 d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-broadcast text-warning fs-5"></i>
                            <h5 className="m-0 fw-bold">4. Monitor de Telemetría en Vivo (J1 vs J2)</h5>
                        </div>
                        <span className={`badge ${phaseInfo.className}`}>{phaseInfo.label}</span>
                    </div>

                    <div className="card-body p-4 bg-light">
                        {/* Banner de Ganador Proclamado */}
                        {liveState.phase === 'finished' && liveState.winnerSide && (
                            <div className="alert alert-success border-success text-center p-3 mb-4 rounded-4 shadow-sm">
                                <div className="display-6 fw-bold mb-1">
                                    🏆 ¡GANADOR:{' '}
                                    {liveState.winnerSide === 'J1' || liveState.winnerSide === 'player1'
                                        ? `JUGADOR 1 (${liveState.player1.name || 'Lado A'})`
                                        : liveState.winnerSide === 'J2' || liveState.winnerSide === 'player2'
                                        ? `JUGADOR 2 (${liveState.player2.name || 'Lado B'})`
                                        : 'EMPATE'}
                                    !
                                </div>
                                <div className="fw-semibold text-success">
                                    Motivo:{' '}
                                    {liveState.winReason === 'FastestReaction' || liveState.winReason === 'FASTEST_TIME'
                                        ? 'Tiempo de reacción más rápido'
                                        : liveState.winReason === 'OpponentEarlyFault' || liveState.winReason === 'OPPONENT_FAULT'
                                        ? 'Falso inicio / Disparo anticipado del contrincante'
                                        : liveState.winReason || 'Victoria confirmada'}
                                </div>
                                {liveState.player1.reactionTimeMs && liveState.player2.reactionTimeMs && (
                                    <div className="small text-muted mt-1 font-monospace">
                                        Diferencia de tiempo:{' '}
                                        <strong>
                                            {Math.abs(liveState.player1.reactionTimeMs - liveState.player2.reactionTimeMs)} ms
                                        </strong>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Selector de Modo de Visualización en Pantalla Gigante */}
                        <div className="bg-white p-3 rounded-3 border mb-4 shadow-sm d-flex flex-wrap align-items-center justify-content-between gap-3">
                            <div>
                                <span className="fw-bold text-dark d-block">
                                    <i className="bi bi-display me-2 text-primary"></i>
                                    Modo de Visualización en Pantalla Gigante (/screen):
                                </span>
                                <small className="text-muted">
                                    Configura si la pantalla gigante muestra 1 solo personaje objetivo para ambos o duelo dual con 2 personajes.
                                </small>
                            </div>
                            <div className="btn-group shadow-sm" role="group" aria-label="Modo de Pantalla">
                                <button
                                    type="button"
                                    className={`btn btn-sm px-3 fw-bold ${
                                        screenDisplayMode === 'single_target' ? 'btn-primary' : 'btn-outline-secondary'
                                    }`}
                                    onClick={() => handleSetDisplayMode('single_target')}
                                >
                                    🎯 Objetivo Único (1 Personaje)
                                </button>
                                <button
                                    type="button"
                                    className={`btn btn-sm px-3 fw-bold ${
                                        screenDisplayMode === 'dual_duel' ? 'btn-primary' : 'btn-outline-secondary'
                                    }`}
                                    onClick={() => handleSetDisplayMode('dual_duel')}
                                >
                                    ⚔️ Duelo Dual (2 Personajes)
                                </button>
                            </div>
                        </div>

                        <div className="row g-4">
                            {/* Box Jugador 1 (Lado A - Azul) */}
                            <div className="col-12 col-md-6">
                                <div className="card border-primary border-2 rounded-4 shadow-sm h-100 bg-white">
                                    <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center p-3">
                                        <div className="d-flex align-items-center gap-2">
                                            <i className="bi bi-person-fill fs-5"></i>
                                            <span className="fw-bold">Jugador 1 (Lado A - Izquierda)</span>
                                        </div>
                                        <span
                                            className={`badge ${
                                                liveState.player1.isConnected ? 'bg-success text-white' : 'bg-secondary'
                                            }`}
                                        >
                                            {liveState.player1.isConnected ? 'CONECTADO' : 'DESCONECTADO'}
                                        </span>
                                    </div>

                                    <div className="card-body p-4 text-center">
                                        {/* Input Editable de Nombre para el Operador */}
                                        <div className="mb-3 text-start">
                                            <label className="form-label small fw-bold text-muted mb-1">Nombre de Jugador 1:</label>
                                            <div className="input-group input-group-sm">
                                                <span className="input-group-text bg-primary text-white">
                                                    <i className="bi bi-person-fill"></i>
                                                </span>
                                                <input
                                                    type="text"
                                                    className="form-control fw-bold"
                                                    placeholder="Nombre Jugador 1"
                                                    value={player1CustomName}
                                                    onChange={(e) => setPlayer1CustomName(e.target.value)}
                                                    onBlur={() => handleSavePlayersAndMode()}
                                                    maxLength={40}
                                                />
                                                <button
                                                    className="btn btn-outline-primary"
                                                    type="button"
                                                    title="Guardar nombre en vivo"
                                                    onClick={() => handleSavePlayersAndMode()}
                                                >
                                                    <i className="bi bi-check-lg"></i>
                                                </button>
                                            </div>
                                        </div>

                                        <div className="my-3 p-3 bg-light rounded-3 border">
                                            <span className="small text-muted d-block mb-1">Tiempo de Reacción:</span>
                                            <div className="display-5 fw-bold text-primary font-monospace">
                                                {liveState.player1.reactionTimeMs !== null && liveState.player1.reactionTimeMs !== undefined
                                                    ? `${liveState.player1.reactionTimeMs} ms`
                                                    : '-- ms'}
                                            </div>
                                        </div>

                                        {liveState.player1.earlyFault && (
                                            <div className="badge bg-danger text-white fs-6 py-2 px-3 rounded-pill shadow-sm">
                                                <i className="bi bi-exclamation-octagon-fill me-1"></i>
                                                ⚠️ FALSO INICIO (Disparo Anticipado)
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Box Jugador 2 (Lado B - Rojo) */}
                            <div className="col-12 col-md-6">
                                <div className="card border-danger border-2 rounded-4 shadow-sm h-100 bg-white">
                                    <div className="card-header bg-danger text-white d-flex justify-content-between align-items-center p-3">
                                        <div className="d-flex align-items-center gap-2">
                                            <i className="bi bi-person-fill fs-5"></i>
                                            <span className="fw-bold">Jugador 2 (Lado B - Derecha)</span>
                                        </div>
                                        <span
                                            className={`badge ${
                                                liveState.player2.isConnected ? 'bg-success text-white' : 'bg-secondary'
                                            }`}
                                        >
                                            {liveState.player2.isConnected ? 'CONECTADO' : 'DESCONECTADO'}
                                        </span>
                                    </div>

                                    <div className="card-body p-4 text-center">
                                        {/* Input Editable de Nombre para el Operador */}
                                        <div className="mb-3 text-start">
                                            <label className="form-label small fw-bold text-muted mb-1">Nombre de Jugador 2:</label>
                                            <div className="input-group input-group-sm">
                                                <span className="input-group-text bg-danger text-white">
                                                    <i className="bi bi-person-fill"></i>
                                                </span>
                                                <input
                                                    type="text"
                                                    className="form-control fw-bold"
                                                    placeholder="Nombre Jugador 2"
                                                    value={player2CustomName}
                                                    onChange={(e) => setPlayer2CustomName(e.target.value)}
                                                    onBlur={() => handleSavePlayersAndMode()}
                                                    maxLength={40}
                                                />
                                                <button
                                                    className="btn btn-outline-danger"
                                                    type="button"
                                                    title="Guardar nombre en vivo"
                                                    onClick={() => handleSavePlayersAndMode()}
                                                >
                                                    <i className="bi bi-check-lg"></i>
                                                </button>
                                            </div>
                                        </div>

                                        <div className="my-3 p-3 bg-light rounded-3 border">
                                            <span className="small text-muted d-block mb-1">Tiempo de Reacción:</span>
                                            <div className="display-5 fw-bold text-danger font-monospace">
                                                {liveState.player2.reactionTimeMs !== null && liveState.player2.reactionTimeMs !== undefined
                                                    ? `${liveState.player2.reactionTimeMs} ms`
                                                    : '-- ms'}
                                            </div>
                                        </div>

                                        {liveState.player2.earlyFault && (
                                            <div className="badge bg-danger text-white fs-6 py-2 px-3 rounded-pill shadow-sm">
                                                <i className="bi bi-exclamation-octagon-fill me-1"></i>
                                                ⚠️ FALSO INICIO (Disparo Anticipado)
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ============================================================ */}
                {/* TARJETA 5: CONFIGURACIÓN DE PARÁMETROS TOUCH                  */}
                {/* ============================================================ */}
                <div className="card border-0 shadow-sm rounded-4 mb-4 overflow-hidden">
                    <div className="card-header bg-dark text-white p-3 px-4 d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-sliders text-warning fs-5"></i>
                            <h5 className="m-0 fw-bold">5. Configuración de Dinámica y Física de Juego</h5>
                        </div>
                    </div>

                    <form onSubmit={handleSaveConfig}>
                        <div className="card-body p-4 bg-white">
                            <div className="row g-4">
                                {/* Switch: Falso Inicio (Early Fault) */}
                                <div className="col-12 col-md-6">
                                    <div className="p-3 bg-light rounded-3 border h-100">
                                        <div className="form-check form-switch fs-5 mb-2">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="switchEarlyFault"
                                                checked={gameConfig.enableEarlyFault}
                                                onChange={(e) =>
                                                    setGameConfig({
                                                        ...gameConfig,
                                                        enableEarlyFault: e.target.checked
                                                    })
                                                }
                                                style={{ cursor: 'pointer' }}
                                            />
                                            <label
                                                className="form-check-label fw-bold text-dark fs-6"
                                                htmlFor="switchEarlyFault"
                                            >
                                                Habilitar Falta por Disparo Anticipado (Early Fault)
                                            </label>
                                        </div>
                                        <p className="text-muted small mb-0">
                                            Si un participante pulsa la pantalla antes de la señal oficial, pierde
                                            automáticamente la ronda otorgando la victoria al rival.
                                        </p>
                                    </div>
                                </div>

                                {/* Switch: Anti-Spam Cooldown */}
                                <div className="col-12 col-md-6">
                                    <div className="p-3 bg-light rounded-3 border h-100">
                                        <div className="form-check form-switch fs-5 mb-2">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="switchSpamCooldown"
                                                checked={gameConfig.enableSpamCooldown}
                                                onChange={(e) =>
                                                    setGameConfig({
                                                        ...gameConfig,
                                                        enableSpamCooldown: e.target.checked
                                                    })
                                                }
                                                style={{ cursor: 'pointer' }}
                                            />
                                            <label
                                                className="form-check-label fw-bold text-dark fs-6"
                                                htmlFor="switchSpamCooldown"
                                            >
                                                Habilitar Enfriamiento Anti-Spam
                                            </label>
                                        </div>
                                        <div className="d-flex align-items-center gap-2 mt-2">
                                            <input
                                                type="number"
                                                className="form-control form-control-sm"
                                                min="50"
                                                max="5000"
                                                step="50"
                                                value={gameConfig.spamCooldownMs}
                                                onChange={(e) =>
                                                    setGameConfig({
                                                        ...gameConfig,
                                                        spamCooldownMs: Number(e.target.value) || 300
                                                    })
                                                }
                                                disabled={!gameConfig.enableSpamCooldown}
                                                style={{ maxWidth: '120px' }}
                                            />
                                            <span className="small text-muted">milisegundos (ms) de espera</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Modo de Medición de Tiempos */}
                                <div className="col-12 col-md-6">
                                    <label className="form-label fw-bold text-dark small mb-1">
                                        Modo de Medición y Sincronización:
                                    </label>
                                    <select
                                        className="form-select"
                                        value={gameConfig.timingMode}
                                        onChange={(e) =>
                                            setGameConfig({
                                                ...gameConfig,
                                                timingMode: e.target.value
                                            })
                                        }
                                        style={{ minHeight: '44px' }}
                                    >
                                        <option value="ServerArrival">1. Llegada Servidor (Server Arrival)</option>
                                        <option value="ClientOffsetSync">2. Sincronización NTP (Client Offset Sync)</option>
                                    </select>
                                    <small className="text-muted d-block mt-1">
                                        Llegada Servidor mide el orden de paquetes REST/WS. Sincronización NTP descuenta la
                                        latencia de red local.
                                    </small>
                                </div>

                                {/* Rango de Espera Aleatoria */}
                                <div className="col-12 col-md-6">
                                    <label className="form-label fw-bold text-dark small mb-1">
                                        Rango de Suspenso / Retardo Aleatorio (ms):
                                    </label>
                                    <div className="row g-2">
                                        <div className="col-6">
                                            <div className="input-group">
                                                <span className="input-group-text small">Min</span>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    min="500"
                                                    max="30000"
                                                    step="500"
                                                    value={gameConfig.minWaitDelayMs}
                                                    onChange={(e) =>
                                                        setGameConfig({
                                                            ...gameConfig,
                                                            minWaitDelayMs: Number(e.target.value) || 3000
                                                        })
                                                    }
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="input-group">
                                                <span className="input-group-text small">Max</span>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    min="1000"
                                                    max="60000"
                                                    step="500"
                                                    value={gameConfig.maxWaitDelayMs}
                                                    onChange={(e) =>
                                                        setGameConfig({
                                                            ...gameConfig,
                                                            maxWaitDelayMs: Number(e.target.value) || 6000
                                                        })
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <small className="text-muted d-block mt-1">
                                        Ventana de retardo impredecible entre el fin del conteo (3, 2, 1) y el disparo.
                                    </small>
                                </div>
                            </div>
                        </div>

                        <div className="card-footer bg-light p-3 px-4 d-flex justify-content-end">
                            <button
                                type="submit"
                                className="btn btn-success px-4 fw-bold d-flex align-items-center gap-2"
                                disabled={isSavingConfig}
                                style={{ minHeight: '48px' }}
                            >
                                {isSavingConfig ? (
                                    <>
                                        <div className="spinner-border spinner-border-sm" role="status"></div>
                                        <span>Guardando...</span>
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-check2-circle fs-5"></i>
                                        <span>Guardar Configuración</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>

                {/* ============================================================ */}
                {/* TARJETA 6: HISTORIAL DE DUELOS DISPUTADOS                     */}
                {/* ============================================================ */}
                <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                    <div className="card-header bg-white p-3 px-4 border-bottom d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-clock-history text-primary fs-5"></i>
                            <h5 className="m-0 fw-bold text-dark">6. Historial de Duelos Disputados</h5>
                        </div>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-primary fw-semibold d-flex align-items-center gap-1"
                            onClick={loadMatchHistory}
                            disabled={isLoadingHistory}
                        >
                            <i className={`bi bi-arrow-clockwise ${isLoadingHistory ? 'animate-spin' : ''}`}></i>
                            <span>Actualizar</span>
                        </button>
                    </div>

                    <div className="card-body p-0">
                        {isLoadingHistory ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status"></div>
                                <p className="text-muted small mt-2">Cargando registros históricos...</p>
                            </div>
                        ) : matchesHistory.length === 0 ? (
                            <div className="text-center py-5 text-muted">
                                <i className="bi bi-journal-x fs-1 d-block mb-2"></i>
                                <span>No se han disputado duelos en esta sala todavía.</span>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-hover align-middle mb-0">
                                    <thead className="table-light small text-uppercase">
                                        <tr>
                                            <th className="px-4 py-3">#</th>
                                            <th className="py-3">Fecha / Hora</th>
                                            <th className="py-3">Jugador 1 (Lado A)</th>
                                            <th className="py-3">Jugador 2 (Lado B)</th>
                                            <th className="py-3">Ganador</th>
                                            <th className="py-3">Razón de Victoria</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {matchesHistory.map((m, index) => {
                                            const p1Name = m.player1Name || m.Player1Name || 'J1';
                                            const p2Name = m.player2Name || m.Player2Name || 'J2';
                                            const p1Time = m.player1ReactionTimeMs ?? m.Player1ReactionTimeMs;
                                            const p2Time = m.player2ReactionTimeMs ?? m.Player2ReactionTimeMs;
                                            const p1Fault = Boolean(m.player1EarlyFault ?? m.Player1EarlyFault);
                                            const p2Fault = Boolean(m.player2EarlyFault ?? m.Player2EarlyFault);
                                            const winnerName = m.winnerName || m.WinnerName || m.winnerSide || m.WinnerSide || 'Empate';
                                            const reason = m.winReason || m.WinReason || '--';
                                            const playedAt = m.playedAtUtc || m.PlayedAtUtc
                                                ? new Date(m.playedAtUtc || m.PlayedAtUtc).toLocaleTimeString([], {
                                                      hour: '2-digit',
                                                      minute: '2-digit',
                                                      second: '2-digit'
                                                  })
                                                : '--:--:--';

                                            return (
                                                <tr key={m.id || m.Id || index}>
                                                    <td className="px-4 fw-bold text-muted">{index + 1}</td>
                                                    <td className="font-monospace small">{playedAt}</td>
                                                    <td>
                                                        <span className="fw-semibold text-primary">{p1Name}</span>
                                                        <div className="small font-monospace">
                                                            {p1Fault ? (
                                                                <span className="badge bg-danger-subtle text-danger">Falso Inicio</span>
                                                            ) : p1Time !== null && p1Time !== undefined ? (
                                                                `${p1Time} ms`
                                                            ) : (
                                                                '-- ms'
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className="fw-semibold text-danger">{p2Name}</span>
                                                        <div className="small font-monospace">
                                                            {p2Fault ? (
                                                                <span className="badge bg-danger-subtle text-danger">Falso Inicio</span>
                                                            ) : p2Time !== null && p2Time !== undefined ? (
                                                                `${p2Time} ms`
                                                            ) : (
                                                                '-- ms'
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className="badge bg-success py-2 px-3 fw-bold">
                                                            🏆 {winnerName}
                                                        </span>
                                                    </td>
                                                    <td className="small text-muted">{reason}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal de Creación / Edición de Temas */}
            {isThemeModalOpen && (
                <QuickReactionThemeModal
                    onClose={() => setIsThemeModalOpen(false)}
                    onThemeSaved={handleThemeSavedCallback}
                    themeToEdit={themeToEdit}
                    roomId={activeRoomId}
                />
            )}

            {/* Modal Genérico de Confirmación */}
            {showConfirmModal && confirmModalData && (
                <Confirm
                    message={confirmModalData.message}
                    actionToYesButton={confirmModalData.actionToYesButton}
                    textYesButton={confirmModalData.textYesButton}
                    textNoButton={confirmModalData.textNoButton}
                    setShowModal={setShowConfirmModal}
                    setConfirmData={setConfirmModalData}
                />
            )}
        </SecondaryBase>
    );
};

export default QuickReaction;
